// src/hooks/useNotifications.js
import { useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";
import axios from "../config/axiosInstance";
import { NOTIFICATIONS } from "../config/apiConfig";
import { useSocketRef, useSocketReady } from "../contexts/SocketContext";
import { useApplication } from "../contexts/ApplicationContext";

export default function useNotifications() {
  const qc = useQueryClient();
  const socketRef = useSocketRef();
  const socketReady = useSocketReady();
  const { user } = useApplication();
  const currentUserId = user?.id || user?.userId || user?.responsibleId || null;

  const { data, isLoading, refetch } = useQuery(
    ["notifications", { unreadOnly: false }],
    async () => {
      const res = await axios.get(NOTIFICATIONS.LIST(), {
        params: { limit: 20 },
      });
      return res.data; // { items, unreadCount }
    },
    { staleTime: 15_000 }
  );

  useEffect(() => {
    const s = socketRef.current;
    if (!socketReady || !s) return;

    const onNew = (notif) => {
      qc.setQueryData(["notifications", { unreadOnly: false }], (old) => {
        if (!old) return { items: [notif], unreadCount: 1 };
        const alreadyExists = (old.items || []).some(
          (item) => String(item?.id || "") === String(notif?.id || ""),
        );
        if (alreadyExists) return old;
        return {
          items: [notif, ...old.items].slice(0, 50),
          unreadCount: (old.unreadCount || 0) + 1,
        };
      });
    };

    const onConversationAssigned = (payload) => {
      const conversation = payload?.conversation || payload || {};
      const assignedUserId =
        conversation?.assignedUserId ?? payload?.assignedUserId ?? null;
      if (!currentUserId || !assignedUserId) return;
      if (String(assignedUserId) !== String(currentUserId)) return;

      const conversationKey =
        payload?.conversationKey ||
        payload?.contact ||
        conversation?.conversationKey ||
        conversation?.contact ||
        conversation?.id ||
        null;

      const targetUrl = conversationKey
        ? `/whatsapp?conversationKey=${encodeURIComponent(String(conversationKey))}`
        : "/whatsapp";

      const syntheticNotif = {
        id: `wa-assign-${String(conversationKey || "none")}-${Date.now()}`,
        userId: assignedUserId,
        actorId: null,
        type: "whatsapp_conversation_assigned",
        title: "Nova conversa atribuída a você",
        body: conversationKey
          ? `A conversa ${String(conversationKey)} foi atribuída para seu atendimento.`
          : "Uma nova conversa do WhatsApp foi atribuída para você.",
        entityType: "whatsapp_conversation",
        entityId: conversationKey ? String(conversationKey) : null,
        url: targetUrl,
        isRead: false,
        createdAt: new Date().toISOString(),
      };

      qc.setQueryData(["notifications", { unreadOnly: false }], (old) => {
        if (!old) return { items: [syntheticNotif], unreadCount: 1 };
        const alreadyExists = (old.items || []).some(
          (item) =>
            String(item?.type || "") === "whatsapp_conversation_assigned" &&
            String(item?.entityId || "") === String(syntheticNotif.entityId || "") &&
            item?.isRead === false,
        );
        if (alreadyExists) return old;
        return {
          items: [syntheticNotif, ...old.items].slice(0, 50),
          unreadCount: (old.unreadCount || 0) + 1,
        };
      });
    };
    const onAutomationNotification = (payload) => {
      if (!payload) return;
      const message = String(payload.message || "").trim();
      const cardTitle = String(payload.cardTitle || "").trim();
      const automationName = String(payload.automationName || "").trim();
      const cardId = payload.cardId || null;

      const title = message
        ? message
        : cardTitle
        ? `Ação no card "${cardTitle}"`
        : "Notificação interna";
      const body = message
        ? cardTitle ? `Card: ${cardTitle}` : ""
        : automationName ? `Automação: ${automationName}` : "";

      const syntheticNotif = {
        id: `automation-notif-${cardId || "x"}-${Date.now()}`,
        userId: currentUserId,
        type: "automation_notification",
        title,
        body,
        entityType: "card",
        entityId: cardId ? String(cardId) : null,
        isRead: false,
        createdAt: new Date().toISOString(),
      };

      qc.setQueryData(["notifications", { unreadOnly: false }], (old) => {
        if (!old) return { items: [syntheticNotif], unreadCount: 1 };
        return {
          items: [syntheticNotif, ...old.items].slice(0, 50),
          unreadCount: (old.unreadCount || 0) + 1,
        };
      });
    };

    const onErr = (e) =>
      console.warn("[notif] connect_error:", e?.message || e);

    s.on("notification:new", onNew);
    s.on("whatsapp:conversation-assigned", onConversationAssigned);
    s.on("automation:notification", onAutomationNotification);
    s.on("connect_error", onErr);

    return () => {
      try {
        s.off("notification:new", onNew);
        s.off("whatsapp:conversation-assigned", onConversationAssigned);
        s.off("automation:notification", onAutomationNotification);
        s.off("connect_error", onErr);
      } catch {}
    };
  }, [socketRef, socketReady, qc, currentUserId]);

  const unreadCount = data?.unreadCount ?? 0;
  const items = data?.items ?? [];

  // Notificações sintéticas (criadas aqui a partir de eventos do socket) têm id
  // de texto e não existem no banco: só mexem no cache.
  const isPersisted = (id) => /^\d+$/.test(String(id));

  const markAsRead = async (id) => {
    if (isPersisted(id)) await axios.patch(NOTIFICATIONS.MARK_ONE_READ(id));
    qc.setQueryData(["notifications", { unreadOnly: false }], (old) => {
      if (!old) return old;
      const target = old.items.find((n) => n.id === id);
      // Já lida: não decrementa de novo o contador.
      if (!target || target.isRead) return old;
      return {
        items: old.items.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
        unreadCount: Math.max(0, (old.unreadCount || 0) - 1),
      };
    });
  };

  const deleteNotification = async (id) => {
    if (isPersisted(id)) await axios.delete(NOTIFICATIONS.DELETE_ONE(id));
    qc.setQueryData(["notifications", { unreadOnly: false }], (old) => {
      if (!old) return old;
      const target = old.items.find((n) => n.id === id);
      if (!target) return old;
      return {
        items: old.items.filter((n) => n.id !== id),
        unreadCount: target.isRead
          ? old.unreadCount || 0
          : Math.max(0, (old.unreadCount || 0) - 1),
      };
    });
  };

  const markAllAsRead = async () => {
    await axios.post(NOTIFICATIONS.MARK_ALL_READ);
    qc.setQueryData(["notifications", { unreadOnly: false }], (old) =>
      !old
        ? old
        : {
            items: old.items.map((n) => ({ ...n, isRead: true })),
            unreadCount: 0,
          }
    );
  };

  return {
    isLoading,
    items,
    unreadCount,
    refetch,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };
}
